import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { config } from '@/config';
import { GAME_KIND_LABELS } from '@/utils/groupGameBareme';
import { FilterPills } from '@/components/games/GameResultsPanel';
import Podium, { RankingList } from '@/components/games/Podium';

export const RANKING_VIEW_OPTIONS = [
  ['live', 'En Cours'],
  ['cumul', 'Cumul'],
  ['instance', 'Par Partie'],
];

export function rankingCriteria(tab, gameKind) {
  if (tab === 'cumul') return ['Points cumulés'];
  if (gameKind === 'memory') return ['Nombre de coups'];
  if (gameKind === 'puzzle') return ['Temps réalisé'];
  if (gameKind) return ['Temps réalisé'];
  return [];
}

const API = config.BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function instanceRankingLabel(inst) {
  const kind = GAME_KIND_LABELS[inst?.kind] || inst?.kind || 'Jeu';
  const extra = inst?.theme_label ? ` · ${inst.theme_label}` : '';
  return `${kind}${inst?.level ? ` · ${inst.level}` : ''}${extra}`;
}

export default function SeasonRankingPanel({
  data,
  token,
  meId,
  compact = false,
  currentInstanceId,
  seasonId = null,
  tab: tabProp,
  onTabChange,
  hideMainTabs = false,
  filtersHost = null,
}) {
  const [loaded, setLoaded] = useState(data || null);
  const [tabState, setTabState] = useState(
    (currentInstanceId || data?.current_instance_id) ? 'live' : 'cumul'
  );
  const [kind, setKind] = useState('general');
  const [liveRanking, setLiveRanking] = useState(null);
  const instances = loaded?.instances || [];
  const [instanceId, setInstanceId] = useState(currentInstanceId || instances[0]?.id || '');
  const tab = tabProp ?? tabState;
  const setTab = onTabChange ?? setTabState;
  const liveId = currentInstanceId || loaded?.current_instance_id || null;

  useEffect(() => {
    if (tabProp != null) return;
    setTabState((currentInstanceId || loaded?.current_instance_id) ? 'live' : 'cumul');
  }, [currentInstanceId, loaded?.current_instance_id, tabProp]);

  useEffect(() => {
    if (data) {
      setLoaded(data);
      return undefined;
    }
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const url = seasonId
          ? `${API}/group/games/seasons/${encodeURIComponent(seasonId)}`
          : `${API}/group/games/season`;
        const res = await fetch(url, { headers: authHeaders(token) });
        if (!cancelled && res.ok) setLoaded(await res.json());
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [data, token, seasonId]);

  useEffect(() => {
    if (!instances.some((inst) => inst.id === instanceId)) {
      setInstanceId(currentInstanceId || instances[0]?.id || '');
    }
  }, [instances, instanceId, currentInstanceId]);

  useEffect(() => {
    if (!token || !liveId || tab !== 'live') return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/group/games/${liveId}/ranking`, { headers: authHeaders(token) });
        if (!cancelled && res.ok) setLiveRanking(await res.json());
      } catch {
        /* ignore */
      }
    };
    load();
    const id = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, liveId, tab]);

  const selected = instances.find((inst) => inst.id === instanceId);
  const liveInst = instances.find((inst) => inst.id === liveId);
  const liveKind = liveRanking?.kind || liveInst?.kind || loaded?.current_kind;
  const rows = tab === 'live'
    ? (liveRanking?.rows || liveInst?.rows || [])
    : tab === 'cumul'
      ? (kind === 'general' ? (loaded?.general || []) : (loaded?.by_kind?.[kind] || []))
      : (selected?.rows || []);
  const gameKind = tab === 'live' ? liveKind : tab === 'instance' ? selected?.kind : (kind === 'general' ? null : kind);
  const showMoves = tab !== 'cumul' && gameKind === 'memory';
  const showElapsed = tab !== 'cumul' && !!gameKind && gameKind !== 'memory';
  const criteria = rankingCriteria(tab, gameKind);
  const tabOptions = RANKING_VIEW_OPTIONS;

  const subFilters = (
    <>
      {tab === 'cumul' ? (
        <FilterPills
          value={kind}
          options={[['general', 'Général'], ...Object.entries(GAME_KIND_LABELS)]}
          onChange={setKind}
          compact
          align="end"
        />
      ) : null}
      {tab === 'instance' ? (
        instances.length ? (
          <select
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value)}
            className="h-8 w-72 max-w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-2 text-gray-800 dark:text-gray-100"
          >
            {instances.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {instanceRankingLabel(inst)} · {inst.player_count || 0} joueur{(inst.player_count || 0) !== 1 ? 's' : ''}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-gray-400">Aucune partie générée.</p>
        )
      ) : null}
    </>
  );

  if (!loaded) {
    return <p className="text-xs text-gray-400 mt-3">Chargement du classement…</p>;
  }

  return (
    <div className={compact ? 'mt-3' : 'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4'}>
      {filtersHost
        ? createPortal(subFilters, filtersHost)
        : (
          <div className={`flex flex-col gap-1.5 mb-3 ${compact ? 'items-start' : 'items-end'}`}>
            {hideMainTabs ? null : (
              <FilterPills
                value={tab}
                options={tabOptions}
                onChange={setTab}
                align={compact ? 'start' : 'end'}
                nowrap
              />
            )}
            {subFilters}
          </div>
        )}
      <h4 className="text-[10px] uppercase tracking-wide text-gray-400 text-center mb-1">
        {tab === 'live'
          ? (liveKind ? `Classement en cours — ${GAME_KIND_LABELS[liveKind] || liveKind}` : 'Classement en cours')
          : tab === 'cumul'
            ? (kind === 'general' ? 'Cumul général' : `Cumul ${GAME_KIND_LABELS[kind] || kind}`)
            : (selected ? instanceRankingLabel(selected) : 'Classement')}
      </h4>
      {criteria.length ? (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center mb-2">
          <span className="font-semibold text-gray-600 dark:text-gray-300">Critères : </span>
          {criteria.join(' · ')}
        </div>
      ) : null}
      {tab === 'live' && !liveId ? (
        <p className="text-xs text-gray-400 text-center py-6">Aucun jeu n’est disponible pour le moment</p>
      ) : (
        <>
          <Podium rows={rows} compact={compact} showElapsed={showElapsed} showMoves={showMoves} />
          {!rows.some((r) => r.rank != null) ? (
            <p className="text-xs text-gray-400 text-center py-2">Aucun résultat pour le moment</p>
          ) : null}
          <RankingList
            rows={rows}
            meId={meId}
            skip={3}
            showMoves={showMoves}
            showElapsed={showElapsed}
            showPoints
          />
        </>
      )}
    </div>
  );
}
